import React, { createContext, useState, useContext, useMemo, useEffect, ReactNode } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, GlobalStyles } from '@mui/material';
import { createDeeliverTheme } from '../theme';
import { useUserPreferences } from '../hooks/useUserPreferences';
import {
  GroundId,
  GroundTokens,
  defaultGroundForMode,
  getGround,
  groundCssVars,
  nextGround,
} from '../theme/grounds';

type ThemeMode = 'light' | 'dark';

interface ThemeModeContextType {
  /** light/dark — derived from the ground, kept for everything that only needs the mode. */
  mode: ThemeMode;
  /** The ground the UI is sitting on. */
  ground: GroundTokens;
  /** Step to the next ground in the cycle (four darks, then two lights). */
  cycleGround: () => void;
  /** Legacy light/dark flip. Lands on the first ground of the other mode. */
  toggleTheme: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextType>({
  mode: 'light',
  ground: getGround('mist'),
  cycleGround: () => {},
  toggleTheme: () => {},
});

export const useThemeMode = () => useContext(ThemeModeContext);

/**
 * Fast-path cache of the last rendered mode, read before the backend answers.
 *
 * Deliberately a DIFFERENT key from the legacy `themeMode`: that one is the
 * one-time migration source, which useUserPreferences consumes and deletes.
 * Writing the cache back into it would make the migration re-fire on every load
 * and let this browser's last value outrank the saved server preference.
 */
const THEME_CACHE_KEY = 'themeMode.cache';

/**
 * The chosen ground, per browser. Only the light/dark half of the choice is
 * saved to the backend (the preferences API knows `theme: light | dark` and
 * nothing finer), so the ground itself lives here, like the prototype's picker.
 */
const GROUND_KEY = 'admin.ground';

function readStoredGround(): GroundId | null {
  const g = localStorage.getItem(GROUND_KEY);
  return g ? getGround(g).id : null;
}

export const ThemeModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Init from localStorage for zero-flash cold load. A stored ground wins; else
  // derive one from the cached mode, falling back to the legacy key for
  // sessions that haven't migrated yet.
  const [groundId, setGroundId] = useState<GroundId>(() => {
    const stored = readStoredGround();
    if (stored) return stored;
    const saved = localStorage.getItem(THEME_CACHE_KEY) || localStorage.getItem('themeMode');
    return defaultGroundForMode(saved === 'dark' ? 'dark' : 'light');
  });
  const ground = getGround(groundId);
  const mode = ground.mode;

  const { preferences, isLoaded, updatePreferences } = useUserPreferences();

  /**
   * Sync from the backend once it loads.
   *
   * Gated on `isLoaded` so an unresolved fetch — which reports the DEFAULTS, i.e.
   * light — can never flip a dark session back to light. `useUserPreferences` only
   * reports loaded after a genuine 2xx, so this now waits rather than guessing.
   *
   * A ground this browser has picked outranks the server's light/dark: the
   * server only knows the half of the choice, so letting it win would bounce a
   * Storm blue session to Deep ink on every load.
   */
  useEffect(() => {
    if (isLoaded && preferences.theme !== mode && !readStoredGround()) {
      setGroundId(defaultGroundForMode(preferences.theme));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, preferences.theme]);

  /**
   * Keep the localStorage fast-path in step with whatever mode is actually showing.
   *
   * The one-time migration DELETES `themeMode` once preferences reach the backend,
   * so without this every subsequent cold load started from the 'light' fallback
   * and only snapped to dark after the preferences request came back — a visible
   * white flash on every refresh.
   */
  useEffect(() => {
    localStorage.setItem(THEME_CACHE_KEY, mode);
  }, [mode]);

  const selectGround = (id: GroundId) => {
    const next = getGround(id);
    setGroundId(next.id);
    localStorage.setItem(GROUND_KEY, next.id);
    if (next.mode !== mode) updatePreferences({ theme: next.mode });
    // The mode cache write is handled by the effect above.
  };

  const cycleGround = () => selectGround(nextGround(groundId).id);

  const toggleTheme = () => selectGround(defaultGroundForMode(mode === 'light' ? 'dark' : 'light'));

  const theme = useMemo(() => createDeeliverTheme(ground), [ground]);
  const cssVars = useMemo(() => ({ ':root': groundCssVars(ground) }), [ground]);
  const contextValue = useMemo(
    () => ({ mode, ground, cycleGround, toggleTheme }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ground],
  );

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles styles={cssVars} />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
